// src/detection/detectFiles.ts

import type { SourceFile, FileRole, FileDetectionResult, ParseError } from '../types';
import { 
  hasGerberExtension, 
  isGerberByContent, 
  classifyFileType,
  classifyGerberLayer,
  type GerberCandidate 
} from '../parsers/gerberDetection';

/**
 * File detection system using content sniffing
 * Detects file types based on content patterns, not just filenames
 * Now uses comprehensive Gerber detection from gerberDetection.ts
 */

// ============================================================================
// DETECTION PATTERNS
// ============================================================================

interface DetectionPattern {
  role: FileRole;
  patterns: RegExp[];
  weight: number;
  sampleSize: number;
}

const detectionPatterns: DetectionPattern[] = [
  // PnP patterns
  {
    role: 'pnp-combined',
    patterns: [
      /\bref\s*des\b/i,
      /\bdesignator\b/i,
      /\bposx?\b/i,
      /\bposition\s*x\b/i,
      /\brotation\b/i,
      /\bside\b/i
    ],
    weight: 0.8,
    sampleSize: 1000
  },
  
  // BOM patterns
  {
    role: 'bom-csv',
    patterns: [
      /\bref(s?|des|erence)\b/i,
      /\bqty\b/i,
      /\bquantity\b/i,
      /\bmpn\b/i,
      /\bmanufacturer\b/i,
      /\bvalue\b/i,
      /\bfootprint\b/i
    ],
    weight: 0.8,
    sampleSize: 1000
  }
];

// ============================================================================
// MAIN DETECTION FUNCTION
// ============================================================================

export function detectFileType(
  filename: string,
  content: string | ArrayBuffer
): FileDetectionResult {
  // Get content sample as string
  let contentStr: string;
  if (typeof content === 'string') {
    contentStr = content;
  } else {
    // Try to decode as text, fallback to empty string for binary
    try {
      contentStr = new TextDecoder().decode(content.slice(0, 10000));
    } catch {
      contentStr = '';
    }
  }
  
  // Check for Gerber files first using comprehensive detection
  const gerberExtension = hasGerberExtension(filename);
  const gerberConfidence = isGerberByContent(contentStr);
  
  if (gerberExtension || gerberConfidence > 0.5) {
    // Classify the Gerber layer
    const candidate: GerberCandidate = {
      id: '', // Will be set by caller
      name: filename,
      path: './',
      content: contentStr,
      size: typeof content === 'string' ? content.length : content.byteLength
    };
    
    const classified = classifyGerberLayer(candidate);
    
    // Map classified role to FileRole
    let fileRole: FileRole = 'unknown';
    
    switch (classified.role) {
      case 'copper':
        fileRole = classified.side === 'top' ? 'gerber-top-copper' : 
                   classified.side === 'bottom' ? 'gerber-bottom-copper' : 
                   'gerber-top-copper';
        break;
      case 'soldermask':
        fileRole = classified.side === 'top' ? 'gerber-top-soldermask' : 
                   classified.side === 'bottom' ? 'gerber-bottom-soldermask' : 
                   'gerber-top-soldermask';
        break;
      case 'silkscreen':
        fileRole = classified.side === 'top' ? 'gerber-top-silkscreen' : 
                   classified.side === 'bottom' ? 'gerber-bottom-silkscreen' : 
                   'gerber-top-silkscreen';
        break;
      case 'paste':
        fileRole = classified.side === 'top' ? 'gerber-top-paste' : 
                   classified.side === 'bottom' ? 'gerber-bottom-paste' : 
                   'gerber-top-paste';
        break;
      case 'outline':
        fileRole = 'gerber-edge-cuts';
        break;
      case 'drill':
        fileRole = 'excellon-drill';
        break;
      default:
        fileRole = 'gerber-top-copper'; // Default fallback
    }
    
    return {
      role: fileRole,
      confidence: classified.confidence,
      indicators: [`gerber:${classified.role}`, `side:${classified.side}`, `method:${classified.detectionMethod}`]
    };
  }
  
  // Check for Excellon drill files
  const fileType = classifyFileType(contentStr);
  if (fileType === 'excellon') {
    return {
      role: 'excellon-drill',
      confidence: 0.9,
      indicators: ['excellon:drill']
    };
  }
  
  // Test other patterns (PnP, BOM)
  const scores = new Map<FileRole, number>();
  const indicators = new Map<FileRole, string[]>();
  
  for (const pattern of detectionPatterns) {
    const sample = contentStr.slice(0, pattern.sampleSize);
    let matchCount = 0;
    const matchedPatterns: string[] = [];
    
    for (const regex of pattern.patterns) {
      if (regex.test(sample)) {
        matchCount++;
        matchedPatterns.push(regex.source);
      }
    }
    
    if (matchCount > 0) {
      // Calculate score based on matches and weight
      const score = (matchCount / pattern.patterns.length) * pattern.weight;
      const currentScore = scores.get(pattern.role) || 0;
      scores.set(pattern.role, currentScore + score);
      
      // Store indicators
      const currentIndicators = indicators.get(pattern.role) || [];
      indicators.set(pattern.role, [...currentIndicators, ...matchedPatterns]);
    }
  }
  
  // Find best match
  let bestRole: FileRole = 'unknown';
  let bestScore = 0;
  
  for (const [role, score] of scores.entries()) {
    if (score > bestScore) {
      bestScore = score;
      bestRole = role;
    }
  }
  
  // Only return if confidence is high enough
  const confidence = Math.min(bestScore, 1.0);
  
  if (confidence < 0.3) {
    return {
      role: 'unknown',
      confidence,
      indicators: []
    };
  }
  
  return {
    role: bestRole,
    confidence,
    indicators: indicators.get(bestRole) || []
  };
}

// ============================================================================
// BATCH DETECTION
// ============================================================================

export interface BatchDetectionResult {
  files: SourceFile[];
  errors: ParseError[];
  summary: {
    total: number;
    detected: number;
    unknown: number;
    byType: Record<FileRole, number>;
  };
}

export function detectMultipleFiles(
  files: { name: string; content: string | ArrayBuffer }[]
): BatchDetectionResult {
  const result: BatchDetectionResult = {
    files: [],
    errors: [],
    summary: {
      total: files.length,
      detected: 0,
      unknown: 0,
      byType: {} as Record<FileRole, number>
    }
  };
  
  for (const file of files) {
    try {
      const detection = detectFileType(file.name, file.content);
      
      const sourceFile: SourceFile = {
        id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
        name: file.name,
        path: './',
        size: typeof file.content === 'string' 
          ? new Blob([file.content]).size 
          : file.content.byteLength,
        content: file.content,
        detectedRole: detection.role,
        confidence: detection.confidence,
        parseErrors: []
      };
      
      result.files.push(sourceFile);
      
      // Update summary
      if (detection.role !== 'unknown') {
        result.summary.detected++;
      } else {
        result.summary.unknown++;
      }
      
      result.summary.byType[detection.role] = (result.summary.byType[detection.role] || 0) + 1;
      
    } catch (error) {
      result.errors.push({
        file: file.name,
        message: error instanceof Error ? error.message : 'Detection error',
        severity: 'error'
      });
    }
  }
  
  return result;
}
